import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCirclePlus } from 'lucide-react';
import { Button } from '@/design/components';
import { cn } from '@/lib/cn';
import { usePerm } from '@/access/usePerm';
import { useMe } from '@/auth/useMe';
import { errorMessage } from '@/lib/errors';
import { useEmployees } from '@/features/employees/api';
import { useOpenDirectChannel } from './admin/api';

/**
 * פתיחת שיחה אישית מתוך רשימת הערוצים.
 *
 * נפתחת בלחיצה ולא יושבת פתוחה: רשימת כל עובדי הרשת מעל הערוצים
 * הופכת את המסך למדריך טלפונים, והערוצים הם מה שבאים אליו.
 */
export function NewDirectConversation() {
  const navigate = useNavigate();
  const { me } = useMe();
  const canDirect = usePerm('messages.direct');
  const open = useOpenDirectChannel();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const employees = useEmployees();

  if (!canDirect) return null;

  const term = search.trim();
  const colleagues = (employees.data ?? []).filter(
    (employee) =>
      employee.is_active &&
      employee.id !== me?.employeeId &&
      (term === '' || `${employee.first_name} ${employee.last_name}`.includes(term)),
  );

  if (!isOpen) {
    return (
      <div>
        <Button variant="secondary" onClick={() => setIsOpen(true)}>
          <MessageCirclePlus size={20} strokeWidth={1.75} aria-hidden="true" />
          שיחה אישית
        </Button>
      </div>
    );
  }

  return (
    <div className="grid gap-2 rounded-md border border-line bg-raised p-3">
      <label htmlFor="direct-search" className="text-label font-medium">
        עם מי לדבר
      </label>
      <input
        id="direct-search"
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="חיפוש עובד"
        className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
      />

      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      <ul className="grid max-h-64 list-none gap-1 overflow-y-auto p-0">
        {colleagues.map((employee) => (
          <li key={employee.id}>
            <button
              type="button"
              disabled={open.isPending}
              onClick={() => {
                setError(null);
                open
                  .mutateAsync(employee.id)
                  .then((channelId) => navigate(`/messages/${channelId}`))
                  .catch((caught: unknown) => setError(errorMessage(caught)));
              }}
              className={cn(
                'min-h-11 w-full cursor-pointer rounded-sm border-0 bg-transparent px-3 text-start text-body text-ink',
                'hover:bg-olive-soft',
              )}
            >
              {employee.first_name} {employee.last_name}
              {employee.branches?.name && (
                <span className="text-body-sm text-muted"> · {employee.branches.name}</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      <div>
        <Button variant="ghost" onClick={() => setIsOpen(false)}>
          סגירה
        </Button>
      </div>
    </div>
  );
}
